import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * StatusBadge — the shared status pill used across every external portal
 * (broker / buyer / seller / vendor) and staff status surfaces. Always
 * carries a text label, so status is never conveyed by colour alone
 * (accessibility guardrail). Tones map to the brand status tokens.
 *
 * Generalises the portal-local `StatusPill` (collection-item tones) and
 * the staff `Badge` so there is one badge vocabulary. Pass `tone` for the
 * colour and `children` for the label; pass `title`/`hint` for a
 * "what this means / what's next" tooltip on hover + keyboard focus.
 */

const statusBadgeVariants = cva(
  "inline-flex items-center gap-space-1 whitespace-nowrap rounded-pill border " +
    "px-space-2 py-[2px] text-caption font-medium leading-none align-middle",
  {
    variants: {
      tone: {
        neutral: "bg-white-warm text-gray-mid border-border-light",
        progress: "bg-gold/15 text-onyx border-gold",
        info: "bg-status-info/12 text-status-info border-status-info",
        // Text and border at --status-warning-ink, fill still --status-warning.
        // The amber sized for a fill is not legible as small text on top of
        // itself: text-status-warning on this tint is 2.55:1, well under AA's
        // 4.5:1, and this tone carries every "Concern" verdict in the buyer
        // module. The ink token is 5.37:1 on a white card and 4.97:1 on the warm
        // page background, and reads as the same amber condition.
        warn: "bg-status-warning/15 text-status-warning-ink border-status-warning-ink",
        ok: "bg-status-success/15 text-status-success border-status-success",
        danger: "bg-status-error/12 text-status-error border-status-error",
        muted: "bg-white-warm text-gray-mid border-border-light",
        onyx: "bg-onyx text-white-pure border-onyx",
        // AA-safe text inks on the info / success fills, and a teal — the
        // submission stage pills' blue, green and teal (WSC). The plain `info`
        // and `ok` tones are unchanged for every other badge.
        infoInk: "bg-status-info/12 text-status-info-ink border-status-info",
        okInk: "bg-status-success/15 text-status-success-ink border-status-success",
        teal: "bg-status-teal/12 text-status-teal-ink border-status-teal",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type StatusTone = NonNullable<VariantProps<typeof statusBadgeVariants>["tone"]>;

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusBadgeVariants> {
  /** A short "what this means / what's next" explanation, surfaced on hover + focus. */
  hint?: string;
  /** Strike-through the label (e.g. "no longer needed" / waived). */
  struck?: boolean;
}

export function StatusBadge({
  className,
  tone,
  hint,
  struck,
  title,
  tabIndex,
  children,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(statusBadgeVariants({ tone }), struck && "line-through", className)}
      title={title ?? hint}
      // Focusable only when there's a hint to read, so keyboard users reach it.
      tabIndex={hint != null ? (tabIndex ?? 0) : tabIndex}
      data-tone={tone ?? "neutral"}
      {...props}
    >
      {children}
    </span>
  );
}

export { statusBadgeVariants };
